import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { selectWorkingSite } from '@store/selectors/site.selectors';
import { writeLastSiteId } from '@utils/lastSite';import { ProviderErrorResponse, UnauthorizedError } from '@apis/auth';
import { SiteNotLinkedError, UpstreamError } from '@apis/equipment';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { AssetRegistry } from './AssetRegistry/AssetRegistry';
import { EquipmentSiteMap } from './SiteMap/EquipmentSiteMap';
import { AssetDetail } from './AssetDetail/AssetDetail';
import { useSiteEquipment } from './hooks/useSiteEquipment';
import type { IEquipmentAsset, StatusFilter } from './equipment.types';
import styles from './EquipmentPage.module.css';

export function EquipmentPage() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const workingSite = useAppSelector(selectWorkingSite);
  const [search, setSearch] = useState('');

  // null = "all"; concrete values are `typeNick` strings derived from the response.
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (siteId) writeLastSiteId(siteId);
  }, [siteId]);

  const query = useSiteEquipment(siteId ?? null);
  const assets = useMemo(() => query.data ?? [], [query.data]);
  // The banner is the way out of its own filter, so once it hides (nothing needs maintenance) the filter lets go too.
  const activeStatusFilter =
    statusFilter === 'needsMaintenance' && !assets.some((a) => a.needsMaintenance) ? null : statusFilter;

  const filteredAssets = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (!matchesChips(a, typeFilter, activeStatusFilter)) return false;
      if (!normalizedSearch) return true;
      return (
        a.name.toLowerCase().includes(normalizedSearch) ||
        a.typeNick.toLowerCase().includes(normalizedSearch) ||
        (a.serialNumber?.toLowerCase().includes(normalizedSearch) ?? false) ||
        (a.floorNick?.toLowerCase().includes(normalizedSearch) ?? false)
      );
    });
  }, [assets, typeFilter, activeStatusFilter, search]);

  const selectedAsset = selectedId
    ? assets.find((a) => a.equipmentId === selectedId) ?? null
    : null;

  // Keep the selection while the asset is still in the list.
  const dropSelectionUnlessIn = (type: string | null, status: StatusFilter) => {
    if (selectedAsset && !matchesChips(selectedAsset, type, status)) setSelectedId(null);
  };

  const changeTypeFilter = (value: string | null) => {
    setTypeFilter(value);
    dropSelectionUnlessIn(value, activeStatusFilter);
  };

  const changeStatusFilter = (value: StatusFilter) => {
    setStatusFilter(value);
    dropSelectionUnlessIn(typeFilter, value);
  };

  if (query.isError) {
    const err = query.error;
    if (err instanceof UnauthorizedError) return null; // global redirect handles it
    if (err instanceof ProviderErrorResponse) {
      // Reconnecting is handled globally by the TopBar (visible here), so this
      // is just the shared honest empty state — no in-page reconnect banner.
      return <DataUnavailable />;
    }
    if (err instanceof SiteNotLinkedError) {
      return (
        <div className={styles.errorPanel}>
          <span>{t('equipment.error.siteNotLinked')}</span>
        </div>
      );
    }
    return (
      <div className={styles.errorPanel}>
        <span>
          {err instanceof UpstreamError
            ? t('equipment.error.upstream')
            : t('equipment.error.generic')}
        </span>
        <button type="button" className={styles.retry} onClick={() => query.refetch()}>
          {t('equipment.error.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.body} data-has-detail={selectedAsset !== null}>
      <AssetRegistry
        assets={assets}
        filteredAssets={filteredAssets}
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={changeTypeFilter}
        statusFilter={activeStatusFilter}
        onStatusFilterChange={changeStatusFilter}
        selectedId={selectedId}
        onSelect={setSelectedId}
        isLoading={query.isLoading}
      />

      <EquipmentSiteMap
        siteId={siteId ?? null}
        siteName={workingSite?.name ?? null}
        selectedAsset={selectedAsset}
      />

      {selectedAsset && (
        <AssetDetail
          asset={selectedAsset}
          siteId={siteId ?? null}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function matchesChips(asset: IEquipmentAsset, type: string | null, status: StatusFilter): boolean {
  const statusMatches = status === null || (status === 'needsMaintenance' ? asset.needsMaintenance : asset.status === status);
  return (type === null || asset.typeNick === type) && statusMatches;
}
